/**
 * DangerZone — NTN-staff-only "Permanent delete" panel for a screening.
 *
 * The PM can archive applicants; only NTN staff can physically remove them.
 * This component renders the warning copy + type-DELETE confirmation modal
 * that fires `POST /admin/screenings/{sid}/permanent-delete`. It is mounted
 * inside ScreeningDetailPage's Details tab, behind a `user.role === "ntn_staff"`
 * guard at the caller site.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DangerZone({ screening }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const onPermanentDelete = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/admin/screenings/${screening.id}/permanent-delete`, {
        confirm: "DELETE",
        reason: reason.trim(),
      });
      toast.success("Applicant permanently deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  const canSubmit = confirm === "DELETE" && reason.trim().length >= 3 && !busy;

  return (
    <div className="bg-red-50 ring-1 ring-red-200 rounded-md p-5 mt-6" data-testid="danger-zone">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-4 w-4 text-red-700" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] tracking-widest uppercase font-bold text-red-700">Danger zone · NTN staff only</div>
          <div className="font-serif text-lg text-slate-900 mt-1">Permanently delete this applicant</div>
          <p className="text-sm text-slate-700 leading-relaxed mt-2 max-w-2xl">
            Removes the applicant record, uploaded documents, internal notes, activity history, queue assignments,
            messages, and adverse-action PDFs. The deletion is recorded in the audit log and <strong>cannot be undone</strong>.
            Archive instead if you may want to restore later.
          </p>
          <Button
            variant="outline"
            className="mt-4 border-red-300 text-red-700 hover:bg-red-100"
            onClick={() => setOpen(true)}
            data-testid="danger-zone-open-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete permanently
          </Button>
        </div>
      </div>

      <AlertDialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setConfirm(""); setReason(""); } }}>
        <AlertDialogContent data-testid="danger-zone-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Delete applicant permanently?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                You are about to permanently delete <strong>{screening.applicant_first_name} {screening.applicant_last_name}</strong> ({screening.applicant_email}) and every related artifact.
              </span>
              <span className="block">This cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Reason for deletion (required)</Label>
              <Textarea
                rows={3} maxLength={1000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Duplicate applicant. Original is sid-XXXX."
                className="mt-1.5 rounded-md"
                data-testid="danger-zone-reason"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Type <span className="font-mono text-red-700">DELETE</span> to confirm</Label>
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value.toUpperCase())}
                placeholder="DELETE"
                className="mt-1.5 rounded-md font-mono"
                data-testid="danger-zone-confirm"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="danger-zone-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!canSubmit}
              onClick={onPermanentDelete}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-300"
              data-testid="danger-zone-confirm-btn"
            >
              {busy ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
